import { MessengerAudience } from '../exports/Constants';

/**
 * Who gets the Intercom Messenger on the dashboard. The Messenger is FoodAmigos'
 * support channel, so each provider carries a `messenger_audience` saying which
 * of its users FoodAmigos supports directly:
 *
 * - `everyone` — FoodAmigos itself: every user, restaurant roles included.
 * - `partner_admins` — a white-label partner: its admins only. Its restaurants
 *   are the partner's to support, and the Messenger would break the white label.
 * - `nobody` — a legacy reseller, and any payload that predates the field.
 *
 * See tests/services/tenant/intercomVisibility.test.js.
 */

/**
 * Whether the Messenger should be booted for the current session.
 *
 * - FoodAmigos staff (super admins, global-access staff) always keep it,
 *   whichever tenant they are looking at.
 * - Restaurant roles follow the provider of the restaurant in context — or, with
 *   no restaurant selected (a merchant viewing a multi-location franchise), of
 *   the franchise — so a user with a partner restaurant and a direct one gets it
 *   only on the direct one. They see it only under an `everyone` provider.
 * - Everyone else follows their own provider: all of them under `everyone`, only
 *   the admins under `partner_admins`.
 *
 * Restaurant roles are never decided on their own provider. Until a restaurant or
 * franchise has landed, the acting provider is still the user's own, which need
 * not be their restaurant's; booting on it would load the script and identify
 * the contact for a partner restaurant's user before the check could catch up.
 * So they wait: no provider on the tenant in context means no Messenger.
 *
 * `currentCompany` is persisted, so right after a reload the decision can rest
 * on the user's last restaurant until `/current-company` refreshes it; that only
 * differs from the truth right after the provider's audience was changed.
 */
export function shouldShowIntercom({
  appId,
  isLoggedIn,
  isSuperAdmin,
  hasGlobalAccess,
  isStoreUser,
  isAdmin,
  ownProvider,
  company,
  franchise,
}) {
  if (!appId || !isLoggedIn) {
    return false;
  }

  if (isSuperAdmin || hasGlobalAccess) {
    return true;
  }

  if (isStoreUser) {
    const restaurantProvider = company?.id ? company.service_provider : franchise?.service_provider;

    return restaurantProvider?.messenger_audience === MessengerAudience.Everyone;
  }

  switch (ownProvider?.messenger_audience) {
    case MessengerAudience.Everyone:
      return true;
    case MessengerAudience.PartnerAdmins:
      return isAdmin;
    default:
      return false;
  }
}
